import {UserState} from '../../../shared/UserState'
import {UIAjaxWrapper} from './network/UIAjaxWrapper'
import {UIAPIEndpoints} from './network/UIAPIEndpoints'
import {ArrayUtils, createDeferredPromise, DeferredPromise, delay} from '@colonist/utils'
import {UserStateData} from '../../../shared/UserStateData'
import {StorageWrapper} from './StorageWrapper'
import {AjaxDataTypes} from '../../../shared/AjaxDataTypes'
import {ClientABTests} from '../../../shared/ABTestTypes.ts'

type UserStateChangeListener = (userState: UserState | undefined) => void

export class ClientUserState {
    private static _userState: UserState | undefined
    private static _abTests: ClientABTests | undefined
    private static readonly changeListeners: UserStateChangeListener[] = []

    private static _stale: boolean = true // loaded from client storage, not server
    static isStale(): boolean {
        return this._stale
    }

    static get userState(): UserState | undefined {
        return this._userState
    }

    static get abTests(): ClientABTests {
        return this._abTests ?? {}
    }

    static get haveAbTestsLoaded(): boolean {
        return this._abTests != undefined
    }

    static hasUser(): boolean {
        return this._userState !== undefined
    }

    static readonly waitUntilFresh = createDeferredPromise()
    private static createUserPromise: DeferredPromise<UserStateData | false> | undefined

    private static async _fetchUserState() {
        const userState = await this.fetchUserStateFromServer()
        if(userState == false) return false

        this._stale = false
        this.update(userState)
        this.waitUntilFresh.resolve()
        return true
    }

    private static async fetchUserStateFromServer(): Promise<UserStateData | undefined | false> {
        const getResponse = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiUserState(), undefined)
        this._abTests = undefined
        if(getResponse == undefined) return false
        const userStateGetResponse = getResponse.json as AjaxDataTypes.UserStateResponse

        this._abTests = userStateGetResponse.abTests

        UIAjaxWrapper.csrfToken = userStateGetResponse.csrfToken
        return userStateGetResponse.userState
    }

    private static async createUser(): Promise<UserStateData | false> {
        if(this.createUserPromise != undefined) return this.createUserPromise
        this.createUserPromise = createDeferredPromise()
        try {
            const postResponse = await UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiUserStateCreate(), undefined, undefined)
            const userStatePostResponse = postResponse?.json as AjaxDataTypes.UserStateResponse | undefined
            if(userStatePostResponse?.userState == undefined) {
                this.createUserPromise.resolve(false)
                return this.createUserPromise
            }

            this._abTests = userStatePostResponse?.abTests
            this.createUserPromise.resolve(userStatePostResponse.userState)
            return this.createUserPromise
        } finally {
            this.createUserPromise = undefined
        }
    }

    static async requireUser() {
        if(this.userState != undefined) return

        const userState = await this.createUser()
        if(userState == false) {
            throw new Error('Unable to create user') // TODO display
        }

        this.update(userState)
    }

    static async refresh() {
        await this._fetchUserState()
    }

    static update(userStateData: UserStateData | undefined) {
        const isUserStateExpired = userStateData?.expiresAt && (new Date(userStateData.expiresAt) < new Date())
        if(isUserStateExpired == true) {
            userStateData = undefined
        }

        if(userStateData != undefined) {
            StorageWrapper.setUserState(userStateData)
            this._userState = new UserState(userStateData)
        } else {
            StorageWrapper.clearUserState()
            this._userState = undefined
        }

        for(const changeListener of this.changeListeners) {
            changeListener(ClientUserState._userState)
        }
    }

    static updateShuffleQueueState(shuffleQueueData: {playerCount: number, modeId: number, mapId: number, endDate: string | undefined}) {
        if(this._userState == undefined) return

        const currentUserStateData = StorageWrapper.getUserState() as UserStateData | undefined
        if(currentUserStateData == undefined) return

        if(currentUserStateData.shuffleQueueState != undefined) {
            const updatedUserStateData: UserStateData = {
                ...currentUserStateData,
                shuffleQueueState: {
                    queueData: {
                        playerCount: shuffleQueueData.playerCount,
                        modeId: shuffleQueueData.modeId,
                        mapId: shuffleQueueData.mapId,
                        endDate: shuffleQueueData.endDate,
                    },
                    playerData: {...currentUserStateData.shuffleQueueState.playerData},
                },
            }

            StorageWrapper.setUserState(updatedUserStateData)
            this._userState = new UserState(updatedUserStateData)

            for(const changeListener of this.changeListeners) {
                changeListener(ClientUserState._userState)
            }
        }
    }

    static registerChangeListener(callback: UserStateChangeListener): () => void {
        this.changeListeners.push(callback)
        callback(this._userState)

        return () => {
            ArrayUtils.removeElementFromArray(this.changeListeners, callback)
        }
    }

    static async init(): Promise<boolean> {
        if(this.loadInitialStateFromStorage()) {
            void this.initFetchLoop() // refresh user state in background
            return true
        }

        return this.initFetchLoop()
    }

    private static loadInitialStateFromStorage(): boolean {
        try {
            const cachedUserState = StorageWrapper.getUserState()
            if(cachedUserState == undefined) return false

            this.update(cachedUserState as any as UserStateData)

            return true
        } catch{
            return false
        }
    }

    private static async initFetchLoop(): Promise<boolean> {
        const maxFetchAttempts = 10
        for(let i = 0; i < maxFetchAttempts; i++) {
            const success = await this._fetchUserState()
            if(success) return true

            await delay(100)
        }

        return false
    }
}
